import { FocusLayerSizeV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { PortalContext } from '../../../context/portal-context';
import { useTrackingManager } from '../../../tracking/use-tracking-manager';
import { formatOwnership } from '../../../tracking/tracking-helper';
import { VehicleManagerLayer } from '../layer/vehicle-manager-layer';
import { ErrorType } from '@esportal/portal-service';

export const useVehicleManagerLayer = ({ onError }: { onError: (errorType: ErrorType) => void }) => {
	const portalContext = useContext(PortalContext);
	const userVehicles = portalContext.portalService.getPersonVehicles().getValue();
	const trackingManager = useTrackingManager();
	const [isLayerOpen, setIsLayerOpen] = useState(false);

	const handleLayerOpen = () => {
		trackingManager.trackClubVWButtonClick(
			'Mis coches',
			{ contentId: 'Open Data User cars layer' },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
		setIsLayerOpen(true);
	};

	useEffect(() => {
		if (isLayerOpen) {
			trackingManager.trackClubVWLayerLoad(
				{ contentId: 'Person Vehicles' },
				{ vehicle: formatOwnership(userVehicles?.vehicles) },
			);
		}
	}, [isLayerOpen, trackingManager]);

	const handleLayerCloseClick = useCallback(() => {
		setIsLayerOpen(false);
		trackingManager.trackClubVWLayerCloseClick('Person Vehicles', {
			contentId: 'Person Vehicles',
		});
	}, [trackingManager]);

	const handleLayerAutoClose = useCallback(() => {
		setIsLayerOpen(false);
	}, []);

	// abre el layer de gestión de vehículos en caso de error
	const handleLayerError = useCallback(
		(errorType: ErrorType) => {
			handleLayerAutoClose();
			onError(errorType);
		},
		[handleLayerAutoClose, onError],
	);

	const renderVehicleManagerLayer = useCallback(() => {
		return (
			<PortalContext.Provider value={portalContext}>
				<VehicleManagerLayer
					onClose={handleLayerCloseClick}
					onAutoClose={handleLayerAutoClose}
					onError={handleLayerError}
				/>
			</PortalContext.Provider>
		);
	}, [portalContext, handleLayerCloseClick, handleLayerAutoClose, handleLayerError]);

	useEffect(() => {
		if (!isLayerOpen) return;
		const layer = portalContext.layerManager.openFocusLayer(
			renderVehicleManagerLayer,
			{},
			{
				size: FocusLayerSizeV2.A,
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
			},
		);

		return () => {
			layer.close();
		};
	}, [isLayerOpen, portalContext, renderVehicleManagerLayer]);

	return {
		handleLayerOpen,
		handleLayerAutoClose,
		handleLayerCloseClick,
		isLayerOpen,
	};
};
